import { isParentUser } from 'src/utils/userRole';
import { isNative } from 'src/utils/platform';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  FunctionComponent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FirebaseAuthentication,
  User,
} from '@capacitor-firebase/authentication';
import { useOnlogin, useSignupUser } from 'src/queries/sessions';

import PageSpinner from 'src/designSystem/PageSpinner';
import { useQueryClient } from 'react-query';
import { useDeleteUser } from 'src/queries/users';
import { useRefreshOnForeground } from 'src/hooks/useRefreshOnForeground';
import { AxiosError } from 'axios';
import { useNotification } from 'src/contexts/NotificationContext';
import { updateLocalStorageUserID } from 'src/utils/usersUtils';
import { forgetSharedDevice } from 'src/utils/sharedDevice';
import { getCurrentUserIdToken } from 'src/modules/firebase';
import { clearTutorThreads } from 'src/modules/baoTutorThreads';
import {
  BEACON_DELIVERY,
  identifyUser,
  logAnalyticsEvent,
  resetAnalytics,
} from 'src/modules/analytics/analytics';
import { reportError } from 'src/modules/logs/Sentry';
import {
  loginOneSignal,
  waitForOneSignalUser,
} from 'src/modules/push-notifications/Onesignal';
import { getModuleExternalId } from 'src/utils/external-id';
import {
  initAppsFlyer,
  loginAppsFlyer,
} from 'src/modules/AppsFlyer/AppsFlyer.module';
import { UserType } from 'src/types/user.types';
import { getCountryFromLanguage } from 'src/utils/schoolSystem';
import { ROUTES } from 'src/config/routes';
import { queryKeys } from 'src/queries/keys';
import { useInterval } from 'src/hooks/useInterval';

const TOKEN_REFRESH_INTERVAL_MS = 50 * 60 * 1000;

const refreshToken = async () => {
  try {
    await getCurrentUserIdToken();
  } catch (error) {
    reportError('Failed to refresh token proactively', error);
  }
};

export interface AuthContextType {
  isLoggedIn: boolean;
  setIsLoggedIn: (value: boolean) => void;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  setMode: (mode: AuthMode) => void;
  createAnonymousUser: () => Promise<void>;
  isAnonymous: boolean;
}

export enum AuthMode {
  LOGIN = 'login',
  SIGNUP = 'signup',
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const APPSFLYER_IDENTITY_TIMEOUT_MS = 3_000;

const AuthContextProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [loadingAuth, setLoadingAuth] = useState<boolean>(true);
  const firebaseUser = useRef<User | null>(null);
  const firebaseAnonymousIdToken = useRef<string>(undefined);
  const mode = useRef<AuthMode>(AuthMode.LOGIN);

  const { mutateAsync: createUser } = useSignupUser();
  const { mutateAsync: onLogin } = useOnlogin();
  const { mutateAsync: deleteMyAccount } = useDeleteUser();

  const user_type = useMemo(
    () => searchParams.get('user_type') ?? undefined,
    [searchParams],
  );
  const referral_code = useMemo(
    () => searchParams.get('referral_code') ?? undefined,
    [searchParams],
  );
  const family_code = useMemo(
    () =>
      searchParams.get('family_code') ??
      searchParams.get('join_code') ??
      undefined,
    [searchParams],
  );

  const logout = useCallback(async (): Promise<void> => {
    resetAnalytics();
    await FirebaseAuthentication.signOut();
    forgetSharedDevice();
    setIsLoggedIn(false);
    navigate(ROUTES.ROOT.path);
    queryClient.clear();
  }, [navigate, queryClient]);

  const deleteAccount = useCallback(async (): Promise<void> => {
    resetAnalytics();
    await deleteMyAccount();
    setIsLoggedIn(false);
    updateLocalStorageUserID(null);
    forgetSharedDevice();
    navigate(ROUTES.ROOT.path);
    queryClient.clear();
  }, [deleteMyAccount, navigate, queryClient]);

  // Decide on the persisted user, not on a token refresh: offline, the refresh
  // fails while the session is still valid.
  const reloadUser = async () => {
    try {
      const { user } = await FirebaseAuthentication.getCurrentUser();
      setIsLoggedIn(Boolean(user));
    } catch {
      setIsLoggedIn(false);
    }
    setLoadingAuth(false);
  };

  useEffect(() => {
    if (isNative()) {
      reloadUser();
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    refreshToken();
  }, [isLoggedIn]);

  useInterval(refreshToken, isLoggedIn ? TOKEN_REFRESH_INTERVAL_MS : null);
  useRefreshOnForeground({ isEnabled: isLoggedIn });

  const tryCreateUser = useCallback(
    async (firebaseUser: User) => {
      try {
        const user = await createUser({
          newUserEmail: firebaseUser.email,
          anonymous_token: firebaseAnonymousIdToken.current,
          is_anonymous: firebaseUser.isAnonymous,
          referral_code,
          user_type,
          family_code,
          language: i18n.language,
          country: await getCountryFromLanguage(),
        });
        identifyUser(user);
        loginOneSignal(user);
        if (!firebaseUser.isAnonymous) {
          firebaseAnonymousIdToken.current = undefined;
          if (isParentUser(user)) {
            let appsFlyerTimer: ReturnType<typeof setTimeout> | undefined;
            try {
              await Promise.race([
                (async () => {
                  await initAppsFlyer(UserType.parent);
                  await loginAppsFlyer(user);
                })(),
                new Promise<never>((_, reject) => {
                  appsFlyerTimer = setTimeout(
                    () =>
                      reject(
                        new Error(
                          `AppsFlyer identity timed out after ${APPSFLYER_IDENTITY_TIMEOUT_MS}ms`,
                        ),
                      ),
                    APPSFLYER_IDENTITY_TIMEOUT_MS,
                  );
                }),
              ]);
            } catch (error) {
              reportError('AppsFlyer login before signup event failed', error);
            } finally {
              clearTimeout(appsFlyerTimer);
            }
          }
          const signupMethod = firebaseUser.providerData.length
            ? firebaseUser.providerData[0]?.providerId
            : 'password';
          const waitStartedAt = Date.now();
          // Not awaited: the user is returned now. Lost only if the process dies before any background.
          waitForOneSignalUser(getModuleExternalId(user.id))
            .catch(error => {
              reportError(
                'OneSignal user wait before signup event failed',
                error,
              );
              return 'failed' as const;
            })
            .then(outcome =>
              logAnalyticsEvent(
                'signup',
                {
                  method: signupMethod,
                  onesignal_wait_outcome: outcome,
                  onesignal_wait_ms: Date.now() - waitStartedAt,
                },
                outcome === 'backgrounded' ? BEACON_DELIVERY : undefined,
              ),
            )
            .catch(error => {
              reportError('Signup analytics event failed', error);
            });
        }
        return user;
      } catch (error) {
        if ((error as AxiosError)?.response?.status === 409) {
          logAnalyticsEvent('signin', {
            method: firebaseUser.providerData.length
              ? firebaseUser.providerData[0]?.providerId
              : 'password',
          });
          return;
        }
        await FirebaseAuthentication.deleteUser();
        throw error;
      }
    },
    [createUser, family_code, user_type, referral_code, i18n.language],
  );

  const createAnonymousUser = useCallback(async () => {
    try {
      mode.current = AuthMode.SIGNUP;
      await FirebaseAuthentication.signInAnonymously();
      firebaseAnonymousIdToken.current = await getCurrentUserIdToken();
    } catch (error) {
      reportError('Fail to create anonymous user', error);
      showError({
        message: t('login.errors.defaultError'),
        error,
      });
      setIsLoggedIn(false);
    }
  }, [showError, t]);

  useEffect(() => {
    const handle = FirebaseAuthentication.addListener(
      'authStateChange',
      async ({ user: _firebaseUser }) => {
        if (
          firebaseUser.current &&
          firebaseUser.current.uid !== _firebaseUser?.uid
        ) {
          clearTutorThreads();
        }
        firebaseUser.current = _firebaseUser;
        if (!_firebaseUser) {
          setIsLoggedIn(false);
          setLoadingAuth(false);
          return;
        }

        setLoadingAuth(true);
        try {
          if (mode.current === AuthMode.SIGNUP) {
            await tryCreateUser(_firebaseUser);
          } else if (mode.current === AuthMode.LOGIN) {
            if (firebaseAnonymousIdToken.current) {
              await onLogin({
                anonymous_token: firebaseAnonymousIdToken.current,
              });
            }
            logAnalyticsEvent('signin', {
              method: _firebaseUser.providerData.length
                ? _firebaseUser.providerData[0]?.providerId
                : 'password',
            });
          }
          if (!_firebaseUser.isAnonymous) {
            firebaseAnonymousIdToken.current = undefined;
          }
          await queryClient.refetchQueries(queryKeys.user());
          setIsLoggedIn(true);
        } catch (error) {
          reportError('Fail to handle auth state change', error);
          showError({
            message: t('login.errors.defaultError'),
            error,
          });
          setIsLoggedIn(false);
        }
        setLoadingAuth(false);
        mode.current = AuthMode.LOGIN;
      },
    ).catch(() => undefined);

    return () => {
      void handle.then(resolved => resolved?.remove());
    };
  }, [onLogin, queryClient, showError, t, tryCreateUser]);

  const setMode = useCallback((newMode: AuthMode) => {
    mode.current = newMode;
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      queryClient.clear();
    }
  }, [isLoggedIn, queryClient]);

  const isAnonymous = firebaseUser.current?.isAnonymous ?? false;

  const value = useMemo(
    () => ({
      isLoggedIn,
      setIsLoggedIn,
      logout,
      deleteAccount,
      setMode,
      createAnonymousUser,
      isAnonymous,
    }),
    [isLoggedIn, logout, deleteAccount, setMode, createAnonymousUser, isAnonymous],
  );

  return (
    <AuthContext.Provider value={value}>
      {loadingAuth ? <PageSpinner /> : children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
